import React, { createContext, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import mx from "./mx.json";
import en from "./en.json";

const dictionaries = { mx, en };

const I18nContext = createContext(null);

/**
 * Route naming:
 *  /mx, /mx/politica-de-privacidad, /mx/politica-de-cookies, /mx/terminos-y-condiciones
 *  /en, /en/privacy-policy, /en/cookie-policy, /en/terms-and-conditions
 *  /thank_you (always Spanish)
 */
export const ROUTE_SEGMENTS = {
  privacy: { mx: "politica-de-privacidad", en: "privacy-policy" },
  cookies: { mx: "politica-de-cookies", en: "cookie-policy" },
  terms: { mx: "terminos-y-condiciones", en: "terms-and-conditions" },
};

export const SUPPORTED_LANGS = ["mx", "en"];
export const DEFAULT_LANG = "mx";

export function getLangFromPath(pathname) {
  const seg = pathname.split("/").filter(Boolean)[0];
  return SUPPORTED_LANGS.includes(seg) ? seg : DEFAULT_LANG;
}

function getPageKeyFromPath(pathname, lang) {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length < 2) return "home";
  const seg = parts[1];
  for (const key of Object.keys(ROUTE_SEGMENTS)) {
    if (ROUTE_SEGMENTS[key][lang] === seg) return key;
  }
  return "home";
}

function resolve(dict, key) {
  return key.split(".").reduce((acc, part) => {
    if (acc == null) return acc;
    return acc[part];
  }, dict);
}

export function buildPath(lang, pageKey) {
  if (pageKey === "home") return `/${lang}`;
  const seg = ROUTE_SEGMENTS[pageKey]?.[lang];
  if (!seg) return `/${lang}`;
  return `/${lang}/${seg}`;
}

export function I18nProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = getLangFromPath(location.pathname);
  const dict = dictionaries[lang] || dictionaries[DEFAULT_LANG];

  const t = useMemo(() => {
    return (key, fallback = "") => {
      const val = resolve(dict, key);
      if (val === undefined || val === null) return fallback || key;
      return val;
    };
  }, [dict]);

  const tRaw = useMemo(() => {
    return (key) => resolve(dict, key);
  }, [dict]);

  // Always-Spanish accessor for the Thank You page
  const tMx = useMemo(() => {
    return (key, fallback = "") => {
      const val = resolve(dictionaries.mx, key);
      if (val === undefined || val === null) return fallback || key;
      return val;
    };
  }, []);

  const switchLanguage = (nextLang) => {
    if (!SUPPORTED_LANGS.includes(nextLang) || nextLang === lang) return;
    const pageKey = getPageKeyFromPath(location.pathname, lang);
    const nextPath = buildPath(nextLang, pageKey);
    // Flag on state so ScrollToTop can skip scrolling
    navigate(nextPath, { state: { languageSwitch: true } });
  };

  const value = {
    lang,
    t,
    tRaw,
    tMx,
    switchLanguage,
    buildPath: (pageKey) => buildPath(lang, pageKey),
    otherLang: lang === "mx" ? "en" : "mx",
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
